import { Component } from '@theme/component';

const STORAGE_KEY = 'popup-newsletter-dismissed';
const KLAVIYO_SUBSCRIBE_ENDPOINT = 'https://manage.kmail-lists.com/ajax/subscriptions/subscribe';

/**
 * A popup newsletter component that auto-shows after a configurable delay
 * and can be re-triggered via a floating button. Subscribes emails directly
 * to a Klaviyo list via the Klaviyo subscribe API.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The popup dialog element.
 * @property {HTMLElement} floatingButton - The floating trigger button.
 * @property {HTMLElement} closeButton - The close button.
 * @property {HTMLFormElement} form - The signup form.
 * @property {HTMLInputElement} emailInput - The email input.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLElement} successMessage - The success message element.
 * @property {HTMLElement} errorMessage - The error message element.
 *
 * @extends Component<Refs>
 */
class PopupNewsletter extends Component {
  requiredRefs = ['dialog', 'floatingButton', 'form', 'emailInput', 'submitButton'];

  /** @type {number | undefined} */
  #autoShowTimer;

  /** @type {number} */
  #previousScrollY = 0;

  /** @type {boolean} */
  get #isTestingMode() {
    return this.hasAttribute('data-testing-mode');
  }

  connectedCallback() {
    super.connectedCallback();

    // Don't show in the theme editor — let merchants preview via the floating button
    if (Shopify.designMode) {
      this.refs.floatingButton.hidden = false;
      return;
    }

    // Testing mode: show immediately, ignore dismissal
    if (this.#isTestingMode) {
      this.showPopup();
      return;
    }

    if (this.#isDismissed()) {
      this.refs.floatingButton.hidden = false;
      return;
    }

    const delay = Number(this.dataset.delay) || 5;
    this.#autoShowTimer = window.setTimeout(() => this.showPopup(), delay * 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#autoShowTimer) {
      clearTimeout(this.#autoShowTimer);
    }
  }

  /**
   * Show the popup dialog.
   */
  showPopup() {
    const { dialog, floatingButton } = this.refs;
    if (dialog.open) return;

    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;

      dialog.showModal();
      floatingButton.hidden = true;

      dialog.addEventListener('click', this.#handleDialogClick);
      dialog.addEventListener('keydown', this.#handleDialogKeydown);
    });
  }

  /**
   * Close the popup dialog with animation.
   */
  async #closePopup() {
    const { dialog, floatingButton } = this.refs;
    if (!dialog.open) return;

    dialog.removeEventListener('click', this.#handleDialogClick);
    dialog.removeEventListener('keydown', this.#handleDialogKeydown);

    dialog.classList.add('popup-newsletter__dialog--closing');

    await new Promise((resolve) => {
      dialog.addEventListener('animationend', resolve, { once: true });
      // Fallback if animation doesn't fire
      setTimeout(resolve, 300);
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('popup-newsletter__dialog--closing');
    floatingButton.hidden = false;
  }

  /**
   * Handle dismiss — close popup and save dismissal timestamp.
   */
  handleDismiss() {
    if (!this.#isTestingMode) {
      this.#saveDismissal();
    }
    this.#closePopup();
  }

  /**
   * Handle the floating button click.
   */
  handleFloatingClick() {
    this.showPopup();
  }

  /**
   * Submit the email to Klaviyo's subscribe endpoint.
   *
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const { form, emailInput } = this.refs;
    const email = emailInput.value.trim();
    const listId = this.dataset.listId;
    if (!email || !listId) return;

    this.#hideMessages();
    this.#setLoading(true);

    try {
      const body = new URLSearchParams({
        g: listId,
        email,
        $fields: '$consent',
        $consent: 'email',
      });
      const response = await fetch(KLAVIYO_SUBSCRIBE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'cache-control': 'no-cache',
        },
        body,
      });
      const data = await response.json();
      if (data && data.success) {
        this.#showSuccess();
        form.reset();
        if (!this.#isTestingMode) {
          this.#saveDismissal();
        }
      } else {
        const apiError = data && Array.isArray(data.errors) && data.errors[0];
        this.#showError(apiError || this.#fallbackErrorMessage);
      }
    } catch {
      this.#showError(this.#fallbackErrorMessage);
    } finally {
      this.#setLoading(false);
    }
  }

  /** @type {string} */
  get #fallbackErrorMessage() {
    return this.dataset.errorMessage || 'Something went wrong. Please try again.';
  }

  /**
   * Toggle the submit button's loading state.
   *
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const { submitButton } = this.refs;
    submitButton.disabled = isLoading;
    const { defaultLabel, loadingLabel } = submitButton.dataset;
    if (defaultLabel && loadingLabel) {
      submitButton.textContent = isLoading ? loadingLabel : defaultLabel;
    }
  }

  #hideMessages() {
    const { successMessage, errorMessage } = this.refs;
    if (successMessage) successMessage.hidden = true;
    if (errorMessage) errorMessage.hidden = true;
  }

  #showSuccess() {
    const { successMessage } = this.refs;
    if (!successMessage) return;
    successMessage.hidden = false;
    successMessage.focus?.();
  }

  /**
   * @param {string} text
   */
  #showError(text) {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;
    const textEl = errorMessage.querySelector('.popup-newsletter__message-text') ?? errorMessage;
    textEl.textContent = text;
    errorMessage.hidden = false;
  }

  /**
   * Close when clicking the backdrop (outside the content).
   *
   * @param {MouseEvent} event
   */
  #handleDialogClick = (event) => {
    if (event.target === this.refs.dialog) {
      this.handleDismiss();
    }
  };

  /**
   * Close on Escape key.
   *
   * @param {KeyboardEvent} event
   */
  #handleDialogKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.handleDismiss();
    }
  };

  /**
   * Check if the popup was recently dismissed.
   *
   * @returns {boolean}
   */
  #isDismissed() {
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (!dismissed) return false;

      const dismissedAt = Number(dismissed);
      const dismissalHours = Number(this.dataset.dismissalHours) || 168; // default 7 days
      const hoursElapsed = (Date.now() - dismissedAt) / (1000 * 60 * 60);

      return hoursElapsed < dismissalHours;
    } catch {
      return false;
    }
  }

  /**
   * Save dismissal timestamp to localStorage.
   */
  #saveDismissal() {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Storage unavailable — silently ignore
    }
  }
}

if (!customElements.get('popup-newsletter')) {
  customElements.define('popup-newsletter', PopupNewsletter);
}
